import { observer } from "mobx-react";
import React, { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  IconButton,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import CustomTypography from "../../../components/common/form/CustomTypography";
import { Close as CloseIcon } from "@mui/icons-material";
import { useStores } from "../../../../use-stores";
import dayjs from "dayjs";

interface SwageazyInventoryStockQuantityDetailsPopupProps {
  open: boolean;
  onClose: () => void;
}

const SwageazyInventoryStockQuantityDetailsPopup: React.FC<
  SwageazyInventoryStockQuantityDetailsPopupProps
> = ({ open, onClose }) => {
  const theme = useTheme();
  const { swageazyInventoryStore } = useStores();

  useEffect(() => {
    if (!open) {
      swageazyInventoryStore.productStockDetails = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleDownload = () => {
    const productId = swageazyInventoryStore.selectedProduct;
    if (!productId) {
      return;
    }

    const product =
      swageazyInventoryStore.swageazyInventoryListingResponse.find(
        (p) => p.id === productId,
      );
    const productName = product?.name || "Product";

    const reportUrl = swageazyInventoryStore.getProductTransactionsReportUrl(
      productId,
      productName,
    );
    window.open(reportUrl, "_blank");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        transition: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        backdrop: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            minHeight: "50vh",
            maxHeight: "90vh",
            maxWidth: {
              xs: "80vw",
              sm: "80vw",
              md: "70vw",
              lg: "60vw",
              xl: "50vw",
            },
            width: {
              xs: "80vw",
              sm: "80vw",
              md: "70vw",
              lg: "60vw",
              xl: "50vw",
            },
            margin: { xs: "16px", sm: "32px" },
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
          },
        },
      }}
      sx={{
        zIndex: 2000,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
          height: "100%",
          maxHeight: "90vh",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
          }}
        >
          <Box>
            {/* <CustomTypography
              variant="body2"
              style={{
                color: theme.palette.customColors.greyTextColor,
                marginBottom: 4,
              }}
            >
              Order: Swageazy Inventory
            </CustomTypography> */}
            <CustomTypography variant="heading3">
              {swageazyInventoryStore.swageazyInventoryListingResponse.find(
                (p) => p.id === swageazyInventoryStore.selectedProduct,
              )?.name || "Product"}
            </CustomTypography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.palette.customColors.greyTextColor,
              "&:hover": {
                backgroundColor: theme.palette.customColors.lightGreyColor,
                color: theme.palette.customColors.blackBorder,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            padding: "30px 44px",
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            display: "flex",
            flexDirection: "column",
            gap: 3,
            minHeight: 0,
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.customColors.greyBackground,
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.customColors.greyDarkborder,
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: theme.palette.customColors.greyTextColor,
              },
            },
          }}
        >
          {swageazyInventoryStore.productStockDetails?.total_stock &&
            swageazyInventoryStore.productStockDetails?.total_stock?.length >
              0 && (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 2,
                  }}
                >
                  <CustomTypography
                    variant="heading6"
                    style={{
                      color: theme.palette.customColors.blackBorder,
                      fontWeight: 600,
                    }}
                  >
                    Total Stock
                  </CustomTypography>
                  <IconButton
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.25rem",
                      marginRight: "0.85rem",
                      color: theme.palette.customColors.darkBlue,
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                    }}
                    size="small"
                    onClick={handleDownload}
                  >
                    <CustomTypography
                      variant="heading8"
                      color={theme.palette.customColors.darkBlue}
                    >
                      View Transactions
                    </CustomTypography>
                  </IconButton>
                </Box>
                <TableContainer
                  component={Paper}
                  sx={{
                    borderRadius: "8px",
                    boxShadow: "none",
                    border: `1px solid ${theme.palette.customColors.greyBorder}`,
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow
                        sx={{
                          backgroundColor:
                            theme.palette.customColors.lightGreyColor,
                        }}
                      >
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.customColors.blackBorder,
                            textAlign: "center",
                          }}
                        >
                          Product Name
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.customColors.blackBorder,
                            textAlign: "center",
                          }}
                        >
                          City
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.customColors.blackBorder,
                            textAlign: "center",
                          }}
                        >
                          Warehouse
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.customColors.blackBorder,
                            textAlign: "center",
                          }}
                        >
                          Quantity
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {swageazyInventoryStore.productStockDetails?.total_stock?.map(
                        (stock, index) => (
                          <TableRow
                            key={index}
                            sx={{
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.customColors.lightGreyColor,
                              },
                            }}
                          >
                            <TableCell sx={{ textAlign: "center" }}>
                              {swageazyInventoryStore.swageazyInventoryListingResponse.find(
                                (p) =>
                                  p.id ===
                                  swageazyInventoryStore.selectedProduct,
                              )?.name || "Product"}
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              {stock?.city}
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              {stock?.warehouse}
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              {stock?.quantity}
                            </TableCell>
                          </TableRow>
                        ),
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

          {swageazyInventoryStore.productStockDetails?.variants_stock &&
            swageazyInventoryStore.productStockDetails?.variants_stock?.length >
              0 && (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 2,
                    marginTop: 2,
                  }}
                >
                  <CustomTypography
                    variant="heading6"
                    style={{
                      color: theme.palette.customColors.blackBorder,
                      fontWeight: 600,
                    }}
                  >
                    Variants
                  </CustomTypography>
                </Box>
                <TableContainer
                  component={Paper}
                  sx={{
                    borderRadius: "8px",
                    boxShadow: "none",
                    border: `1px solid ${theme.palette.customColors.greyBorder}`,
                    overflowX: "auto",
                    WebkitOverflowScrolling: "touch",
                  }}
                >
                  <Table sx={{ minWidth: 800 }}>
                    <TableHead>
                      <TableRow
                        sx={{
                          backgroundColor:
                            theme.palette.customColors.lightGreyColor,
                        }}
                      >
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.customColors.blackBorder,
                            textAlign: "center",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Sku
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.customColors.blackBorder,
                            textAlign: "center",
                            whiteSpace: "nowrap",
                          }}
                        >
                          City
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.customColors.blackBorder,
                            textAlign: "center",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Warehouse
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.customColors.blackBorder,
                            textAlign: "center",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Box Id
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.customColors.blackBorder,
                            textAlign: "center",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Rack
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.customColors.blackBorder,
                            textAlign: "center",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Quantity
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.customColors.blackBorder,
                            textAlign: "center",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Expiry Date
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {swageazyInventoryStore.productStockDetails?.variants_stock?.map(
                        (variant, index) => (
                          <TableRow
                            key={index}
                            sx={{
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.customColors.lightGreyColor,
                              },
                            }}
                          >
                            <TableCell sx={{ textAlign: "center" }}>
                              {variant?.sku}
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              {variant?.city}
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              {variant?.warehouse}
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              {variant?.box_id}
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              {variant?.rack}
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              {variant?.quantity}
                            </TableCell>
                            <TableCell
                              sx={{
                                textAlign: "center",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {variant?.expiry_date &&
                              dayjs(variant.expiry_date).isValid()
                                ? dayjs(variant.expiry_date).format(
                                    "DD MMM YYYY",
                                  )
                                : variant?.expiry_date ?? "—"}
                            </TableCell>
                          </TableRow>
                        ),
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

          {swageazyInventoryStore.productStockDetails?.blocked_stock &&
            swageazyInventoryStore.productStockDetails?.blocked_stock?.length >
              0 && (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 2,
                    marginTop: 2,
                  }}
                >
                  <CustomTypography
                    variant="heading6"
                    style={{
                      color: theme.palette.error.main,
                      fontWeight: 600,
                    }}
                  >
                    Blocked Stock
                  </CustomTypography>
                </Box>
                <TableContainer
                  component={Paper}
                  sx={{
                    borderRadius: "8px",
                    boxShadow: "none",
                    border: `1px solid ${theme.palette.customColors.greyBorder}`,
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow
                        sx={{
                          backgroundColor:
                            theme.palette.customColors.lightGreyColor,
                        }}
                      >
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.customColors.blackBorder,
                            textAlign: "center",
                          }}
                        >
                          Product Name
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.customColors.blackBorder,
                            textAlign: "center",
                          }}
                        >
                          City
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.customColors.blackBorder,
                            textAlign: "center",
                          }}
                        >
                          Warehouse
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.customColors.blackBorder,
                            textAlign: "center",
                          }}
                        >
                          Pr Id
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.customColors.blackBorder,
                            textAlign: "center",
                          }}
                        >
                          Quantity
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {swageazyInventoryStore.productStockDetails?.blocked_stock?.map(
                        (blocked, index) => (
                          <TableRow
                            key={index}
                            sx={{
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.customColors.lightGreyColor,
                              },
                            }}
                          >
                            <TableCell sx={{ textAlign: "center" }}>
                              {swageazyInventoryStore.swageazyInventoryListingResponse.find(
                                (p) =>
                                  p.id ===
                                  swageazyInventoryStore.selectedProduct,
                              )?.name || "Product"}
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              {blocked?.city}
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              {blocked?.warehouse}
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              {blocked?.pr_id}
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              {blocked?.quantity}
                            </TableCell>
                          </TableRow>
                        ),
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

          {!swageazyInventoryStore.productStockDetails?.total_stock &&
            !swageazyInventoryStore.productStockDetails?.variants_stock &&
            !swageazyInventoryStore.productStockDetails?.blocked_stock && (
              <CustomTypography variant="body2">
                No details found
              </CustomTypography>
            )}
        </DialogContent>
      </Box>
    </Dialog>
  );
};

export default observer(SwageazyInventoryStockQuantityDetailsPopup);
